import React, { useState, useEffect } from "react";
import StateSelector from "../components/StateSelector";
import CountySelector from "../components/CountySelector";
import DeviceSelector from "../components/DeviceSelector";
import type { State, County } from "../../../shared/types/models/location";
import type { Device } from "../../../shared/types/models/device";
import { useUserContext } from "../config/UserContext";

const SettingsPage: React.FC = () => {
  // State/County defaults
  const [state, setState] = useState<State | null>(null);
  const [county, setCounty] = useState<County | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [showCleared, setShowCleared] = useState(false);

  // Device default
  const [device, setDevice] = useState<Device | null>(null);
  const [showDeviceSaved, setShowDeviceSaved] = useState(false);
  const [showDeviceCleared, setShowDeviceCleared] = useState(false);

  // Load defaults on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('searchDefaults');
      if (saved) {
        const { state: savedState, county: savedCounty } = JSON.parse(saved);
        if (savedState) setState(savedState);
        if (savedCounty) setCounty(savedCounty);
      }
    } catch {}
    try {
      const savedDevice = localStorage.getItem('deviceDefault');
      if (savedDevice) {
        setDevice(JSON.parse(savedDevice));
      }
    } catch {}
  }, []);

  // Save defaults
  const handleSetDefaults = () => {
    if (state && county) {
      localStorage.setItem('searchDefaults', JSON.stringify({ state, county }));
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    }
  };
  const handleClearDefaults = () => {
    localStorage.removeItem('searchDefaults');
    setState(null);
    setCounty(null);
    setShowCleared(true);
    setTimeout(() => setShowCleared(false), 2000);
  };

  // Save/clear device
  const handleSetDeviceDefault = () => {
    if (device) {
      localStorage.setItem('deviceDefault', JSON.stringify(device));
      setShowDeviceSaved(true);
      setTimeout(() => setShowDeviceSaved(false), 2000);
    }
  };
  const handleClearDeviceDefault = () => {
    localStorage.removeItem('deviceDefault');
    setDevice(null);
    setShowDeviceCleared(true);
    setTimeout(() => setShowDeviceCleared(false), 2000);
  };

  return (
    <div className="max-w-[1400px] mx-auto bg-white px-8 py-8 mt-8 flex flex-col gap-8">
      <h1 className="text-2xl font-bold text-[#141435] mb-6 font-plus-jakarta">Settings</h1>
      
      <div className="w-[700px] max-w-full mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 w-full">
          {/* State & County Section */}
          <div className="flex-1 flex flex-col gap-2 min-w-[320px]">
            <h2 className="text-lg font-semibold mb-2">Default State & County</h2>
            <StateSelector value={state} onChange={setState} label="State" dropdownAnchor="bottom" filter={{ enabled: true }} />
            <CountySelector value={county} onChange={setCounty} state={state} label="County" dropdownAnchor="bottom" />
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                className="text-xs text-[#0B4BB6] font-plus-jakarta px-2 py-1 bg-[#F4F8FF] rounded hover:bg-[#E5EDFA] border border-[#D7DFEC] shadow-sm"
                style={{ minWidth: 0, fontWeight: 500, boxShadow: 'none', lineHeight: 1.1 }}
                onClick={handleSetDefaults}
                disabled={!state || !county}
              >
                Set Defaults
              </button>
              <button
                type="button"
                className="text-xs text-[#B60B0B] font-plus-jakarta px-2 py-1 bg-[#FFF4F4] rounded hover:bg-[#FAE5E5] border border-[#FFD7D7] shadow-sm"
                style={{ minWidth: 0, fontWeight: 500, boxShadow: 'none', lineHeight: 1.1 }}
                onClick={handleClearDefaults}
              >
                Clear Defaults
              </button>
            </div>
            {showSaved && <span className="text-green-600 text-[10px] mt-1">Defaults saved!</span>}
            {showCleared && <span className="text-red-600 text-[10px] mt-1">Defaults cleared!</span>}
          </div>
          
          {/* Splitter */}
          <div className="hidden lg:flex h-auto mx-4">
            <div className="border-l border-gray-300 h-full" style={{ minHeight: 220 }} />
          </div>
          
          {/* Device Section */}
          <div className="flex-1 flex flex-col gap-2 min-w-[320px]">
            <h2 className="text-lg font-semibold mb-2">Default Device</h2>
            <DeviceSelector value={device} onChange={setDevice} label="Device" dropdownAnchor="bottom" />
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                className="text-xs text-[#0B4BB6] font-plus-jakarta px-2 py-1 bg-[#F4F8FF] rounded hover:bg-[#E5EDFA] border border-[#D7DFEC] shadow-sm"
                style={{ minWidth: 0, fontWeight: 500, boxShadow: 'none', lineHeight: 1.1 }}
                onClick={handleSetDeviceDefault}
                disabled={!device}
              >
                Set Default
              </button>
              <button
                type="button"
                className="text-xs text-[#B60B0B] font-plus-jakarta px-2 py-1 bg-[#FFF4F4] rounded hover:bg-[#FAE5E5] border border-[#FFD7D7] shadow-sm"
                style={{ minWidth: 0, fontWeight: 500, boxShadow: 'none', lineHeight: 1.1 }}
                onClick={handleClearDeviceDefault}
              >
                Clear Default
              </button>
            </div>
            {showDeviceSaved && <span className="text-green-600 text-[10px] mt-1">Default device saved!</span>}
            {showDeviceCleared && <span className="text-red-600 text-[10px] mt-1">Default device cleared!</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage; 